import { CloseHealth } from "@common/types/closeHealth";
import { ClientsWorkOverviewColumn } from "@common/types/views/clientsWorkOverviewView";
import type { ColumnOptions, View } from "@common/types/views/views";
import { separateColumnsByPin } from "@design-system/ViewToolbar/columnSettingsUtils";

/**
 * The List columns a card can show as tags. The entity is the title, client
 * and service share the context line, the engagement team sits in the
 * presence stack, and actions never show.
 */
export const CARD_FIELD_COLUMNS = [
    ClientsWorkOverviewColumn.CLOSE_START,
    ClientsWorkOverviewColumn.CLOSE_END,
    ClientsWorkOverviewColumn.SERVICE_PERIOD,
    ClientsWorkOverviewColumn.WORK_START_DATE,
    ClientsWorkOverviewColumn.WORK_DELIVERY_DATE,
    ClientsWorkOverviewColumn.CLOSE_STAGE,
    ClientsWorkOverviewColumn.HEALTH,
    ClientsWorkOverviewColumn.MY_ROLE,
    ClientsWorkOverviewColumn.TIME_IN_STAGE,
    ClientsWorkOverviewColumn.TASK_PROGRESS,
    ClientsWorkOverviewColumn.ESTIMATED_TIME,
    ClientsWorkOverviewColumn.APPLICATION_TYPE,
    ClientsWorkOverviewColumn.APPLIED_BY,
    ClientsWorkOverviewColumn.APPLIED_AT,
    ClientsWorkOverviewColumn.UNREAD_TASKS,
] as const;

export type WorkCardFieldId = (typeof CARD_FIELD_COLUMNS)[number];

/** A column the card shows as a tag, with its List options for the cell variant. */
export interface WorkCardFieldColumn {
    id: WorkCardFieldId;
    options?: ColumnOptions | null;
}

const CARD_FIELD_IDS: ReadonlySet<string> = new Set(CARD_FIELD_COLUMNS);

const isWorkCardFieldId = (id: string): id is WorkCardFieldId =>
    CARD_FIELD_IDS.has(id);

const isWorkCardField = (
    column: View["columns"][number],
): column is WorkCardFieldColumn => isWorkCardFieldId(column.id);

const getTagRank = ({ id }: WorkCardFieldColumn) =>
    id === ClientsWorkOverviewColumn.CLOSE_STAGE ? 0 : 1;

/** The visible columns a card can show: the stage (or Done or Canceled) first, then List's order. */
const getVisibleFieldColumns = (columns: View["columns"]) => {
    const { leftPinnedColumns, centerColumns, rightPinnedColumns } =
        separateColumnsByPin(columns);
    return [...leftPinnedColumns, ...centerColumns, ...rightPinnedColumns]
        .filter(isWorkCardField)
        .toSorted((first, second) => getTagRank(first) - getTagRank(second));
};

/**
 * The tags a view's cards show, less `hiddenFields` (what the columns and
 * lanes already show). A Kick view's `cardFieldIds` are a fixed list, in
 * order, with List's options where the column is there; without them, the
 * visible columns a card can show.
 */
export const getWorkCardFieldColumns = ({
    columns,
    cardFieldIds,
    hiddenFields,
}: {
    columns: View["columns"];
    cardFieldIds: readonly string[] | undefined;
    hiddenFields: ReadonlySet<string>;
}): WorkCardFieldColumn[] => {
    const fieldColumns = cardFieldIds
        ? cardFieldIds.filter(isWorkCardFieldId).map((id) => ({
              id,
              options: columns.find((column) => column.id === id)?.options,
          }))
        : getVisibleFieldColumns(columns);
    return fieldColumns.filter(({ id }) => !hiddenFields.has(id));
};

/** Whether cards with these fields show Health in Progress's place on work that has a health. */
export const isProgressSwappedForHealth = (fieldIds: readonly string[]) =>
    fieldIds.includes(ClientsWorkOverviewColumn.TASK_PROGRESS) &&
    !fieldIds.includes(ClientsWorkOverviewColumn.HEALTH);

/**
 * Progress makes way for Health on work that has a
 * health, unless the card already shows Health.
 */
export const withHealthForProgress = ({
    fieldColumns,
    health,
}: {
    fieldColumns: readonly WorkCardFieldColumn[];
    health: CloseHealth;
}): readonly WorkCardFieldColumn[] =>
    health === CloseHealth.NONE ||
    !isProgressSwappedForHealth(fieldColumns.map(({ id }) => id))
        ? fieldColumns
        : fieldColumns.map((column) =>
              column.id === ClientsWorkOverviewColumn.TASK_PROGRESS
                  ? { id: ClientsWorkOverviewColumn.HEALTH }
                  : column,
          );
